/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnDestroy, type OnInit, Component, Injector, Input, TemplateRef, ViewChild } from '@angular/core';
import { type MenuItem } from '@dm/ang';
import { type ColumnDef, type TableConfig, EventService, HttpService, SlideWindowAbstract } from '@dm/core';
import { SWITCH } from 'app/config';
import { type Subscription } from 'rxjs';

import { SystemUpgradeComponent } from '../system-upgrade-panel/system-upgrade.component';
import { SystemUpgradeUploadComponent } from '../system-upgrade-upload/system-upgrade-upload.component';
import { Progress, UpgradeModeE, UpgradeProcesses } from '../upgrade.const';
import { UpgradeService } from '../upgrade.service';
import { UpgradeComponent } from '../upgrade-panel/upgrade.component';
import { UpgradeStrService } from '../upgrade-str.service';
import { UpgradeUploadComponent } from '../upload/upgrade-upload.component';

@Component({
    selector: 'app-upgrade-upload-entry',
    templateUrl: './upgrade-upload-entry.component.html',
    styleUrls: ['./upgrade-upload-entry.component.scss'],
})

export class UpgradeUploadEntryComponent extends SlideWindowAbstract implements OnInit, OnDestroy {
    /**
     * 热补丁升级面板组件
     */
    @Input() upgrade: UpgradeComponent;
    /**
     * 系统升级面板组件
     */
    @Input() systemUpgrade: SystemUpgradeComponent;
    /**
     * 升级模式
     */
    @Input() upgradeMode?: UpgradeModeE = UpgradeModeE.HotPatch;

    @ViewChild('upgradeEl') upgradeEl: UpgradeUploadComponent;
    @ViewChild('systemUpgradeEl') systemUpgradeEl: SystemUpgradeUploadComponent;
    @ViewChild('upgradePrompt') upgradePrompt: TemplateRef<any>;

    UpgradeModeE = UpgradeModeE;

    // 上传窗口显示
    show = true;

    // 是否展示系统升级
    isShowSystemUpgrade = true;

    // 当前版本
    currentVersion: string;

    i18n = this.str.i18n;

    sub$: Subscription;

    steps: MenuItem[];

    activeIndex = 0;

    upgradePromptColumnDefs: ColumnDef[];
    upgradePromptConfig: TableConfig;

    // 高危提示表格数据
    upgradePromptData: any[];

    isJa = this.i18nService.language === 'ja';

    SWITCH = SWITCH;

    // 当时是否正在进行升级
    get isUpgrading(): boolean {
        if (!this.upgrade) {
            return false;
        }
        return Boolean(this.upgrade.checkResultPolling) || Boolean(this.upgrade.upgradeProgressPolling);
    }

    constructor(
        injector: Injector,
        private str: UpgradeStrService,
        private http: HttpService,
        private upgradeService: UpgradeService,
        private eventService: EventService,
    ) {
        super(injector);
    }

    ngOnInit(): void {
        this.pageReady = true;
        this.windowVisible = true;
        this.getCurrentVersion();
        this.initDeviceInfo();
        this.initTable();
        if (this.upgradeMode === UpgradeModeE.System) {
            this.initSteps();
        }
    }

    initTable(): void {
        this.upgradePromptConfig = {
            rows: null,
        };
        this.upgradePromptColumnDefs = [
            {
                field: 'currentVersion',
                header: this.i18n.currentVersion,
            },
            {
                field: 'packageTargetVersion',
                header: this.i18n.upgradePackageVersion,
            },
            {
                field: 'hotpatchTargetVersion',
                header: this.i18n.patchVersion,
            },
            {
                field: 'upgradeMode',
                header: this.i18n.UpgradeMode,
            },
        ];
    }

    initDeviceInfo(): void {
        this.sub$ = this.eventService.subscribe('systemInfoUpdate', this.getIsShowUpgrade);
        this.getIsShowUpgrade();
    }

    /**
     * 步骤初始化
     */
    initSteps(): void {
        this.steps = [
            {
                label: this.i18n.preUpGradeCheck,
                command: (): void => {
                    this.activeIndex = 0;
                },
            },
            {
                label: this.i18n.backupLab,
                command: (): void => {
                    this.activeIndex = 1;
                },
            },
            {
                label: this.i18n.upgrade,
                command: (): void => {
                    this.activeIndex = 2;
                },
            },
        ];
    }

    /**
     * 是否展示系统升级， 高端release屏蔽系统升级入口
     */
    getIsShowUpgrade = (): void => {
        this.isShowSystemUpgrade = this.upgradeService.ifShowUpgrade();
        if (this.isShowSystemUpgrade !== undefined) {
            this.sub$.unsubscribe();
        }
    };

    // 获取当前版本
    getCurrentVersion(): void {
        this.http.get(this.http.makeUrl('system.get')).subscribe((result) => {
            this.currentVersion = result.data?.SoftwarePatch ?? result.data?.pointRelease;
        });
    }

    async onUpgrade(): Promise<any> {
        if (this.upgradeMode === UpgradeModeE.HotPatch) {
            this.upgrade.visible = false;
            await this.upgrade.precheck();
            if (this.upgradeService.getPrechekStatus() === 'systemUpgrade') {
                this.systemUpgrade.iconVisible = false;
                this.systemUpgrade.visible = false;
            }
            this.close();
        } else if (this.upgradeMode === UpgradeModeE.System) {
            this.upgradePromptData = [
                {
                    currentVersion: this.currentVersion,
                    packageTargetVersion: this.systemUpgradeEl.packageTargetVersion,
                    hotpatchTargetVersion: this.systemUpgradeEl.hotpatchTargetVersion,
                    upgradeMode: this.i18n.rapidUpgrade,
                },
            ];
            this.promptService.danger({
                title: this.i18n.upgradeTaskConfirm,
                width: 700,
                content: this.i18n.targetVersionWrong,
                templateRef: this.upgradePrompt,
                onOk: async () => {
                    this.promptService.close();
                    this.systemUpgrade.visible = false;
                    await this.systemUpgrade.precheck();
                    if (this.upgradeService.getPrechekStatus() === 'upgrade') {
                        this.upgrade.iconVisible = false;
                        this.upgrade.visible = false;
                    }
                    this.close();
                },
            });
        }
    }

    upgradeBtnDisabled(upgradeEl: UpgradeUploadComponent, systemUpgradeEl: SystemUpgradeComponent, upDisBtn, sysUpDisBtn): boolean {
        if (upgradeEl && upDisBtn) {
            return true;
        }

        if (systemUpgradeEl && sysUpDisBtn) {
            return true;
        }

        return false;
    }

    upgradeBtnDisabledTips = (hotpatchUploadEnable: boolean): string => {
        if (this.upgradeMode === UpgradeModeE.HotPatch) {
            return '';
        }

        if (hotpatchUploadEnable) {
            return this.i18n.uploadUpgradeandHotPackageTip;
        } else {
            return this.i18n.uploadUpgradePackageTip;
        }
    };

    /**
     * 上一步
     */
    previous(): void {
        if (this.activeIndex > 0) {
            this.activeIndex--;
            this.systemUpgradeEl.previous();
        }
    }

    /**
     * 上一步按钮灰化
     */
    prevBtnDisabled = (currentProcess: Progress, backupLoading: boolean): boolean => {
        // 备份导出中，上传包中按钮灰化
        if (backupLoading || [Progress.packageUploading, Progress.hotpatchUploading].includes(currentProcess)) {
            return true;
        }
        return false;
    };

    /**
     * 下一步
     */
    next(): void {
        if (this.activeIndex !== this.steps.length - 1) {
            this.activeIndex++;
            this.systemUpgradeEl.next();
        }
    }

    /**
     * 下一步按钮灰化
     */
    nextBtnDisabled = (activeIndex: number, currentProcess: Progress, isShowExport: boolean, isExportSucceed: boolean,
        backupLoading: boolean): boolean => {
        if (activeIndex === 0 && (currentProcess !== Progress.evaluateSuc && !UpgradeProcesses.includes(currentProcess))) {
            return true;
        }
        if (activeIndex === 1 && (backupLoading || !this.systemUpgradeEl.upgradeShow(currentProcess, isExportSucceed))) {
            return true;
        }
        return false;
    };

    /**
     * 下一步按钮灰化tip
     */
    nextBtnDisabledTips = (activeIndex: number): string => {
        if (activeIndex === 0) {
            return this.i18n.beforeUpgradeCheck;
        }
        return this.i18n.beforeUpgrade;
    };

    ngOnDestroy(): void {
        this.sub$.unsubscribe();
    }
}
